const legacyRoot =
  typeof self !== "undefined"
    ? self
    : typeof window !== "undefined"
      ? window
      : undefined;

if (legacyRoot && typeof (legacyRoot as { globalThis?: unknown }).globalThis === "undefined") {
  Object.defineProperty(legacyRoot, "globalThis", {
    configurable: true,
    value: legacyRoot,
    writable: true
  });
}

if (!Array.prototype.at) {
  Object.defineProperty(Array.prototype, "at", {
    configurable: true,
    value(index: number) {
      const length = this.length >>> 0;
      let relativeIndex = Math.trunc(Number(index)) || 0;
      if (relativeIndex < 0) relativeIndex += length;
      if (relativeIndex < 0 || relativeIndex >= length) return undefined;
      return this[relativeIndex];
    },
    writable: true
  });
}

if (!String.prototype.replaceAll) {
  Object.defineProperty(String.prototype, "replaceAll", {
    configurable: true,
    value(searchValue: string | RegExp, replaceValue: string) {
      const source = String(this);
      if (searchValue instanceof RegExp) {
        if (!searchValue.global) throw new TypeError("String.prototype.replaceAll called with a non-global RegExp");
        return source.replace(searchValue, replaceValue);
      }
      return source.split(String(searchValue)).join(String(replaceValue));
    },
    writable: true
  });
}

if (!Object.fromEntries) {
  Object.defineProperty(Object, "fromEntries", {
    configurable: true,
    value(entries: Iterable<readonly [PropertyKey, unknown]>) {
      const output: Record<PropertyKey, unknown> = {};
      for (const [key, value] of entries) {
        output[key] = value;
      }
      return output;
    },
    writable: true
  });
}

if (!Promise.allSettled) {
  Object.defineProperty(Promise, "allSettled", {
    configurable: true,
    value<T>(values: Iterable<T | PromiseLike<T>>) {
      return Promise.all(
        Array.from(values).map((value) =>
          Promise.resolve(value).then(
            (resolved) => ({ status: "fulfilled", value: resolved }),
            (reason) => ({ status: "rejected", reason })
          )
        )
      );
    },
    writable: true
  });
}

type RoundRectRadius = number | { x?: number; y?: number };

function normalizeRoundRectRadii(radius?: RoundRectRadius | RoundRectRadius[]) {
  const source = Array.isArray(radius) ? radius : [radius ?? 0];
  const values = source.map((item) => {
    if (typeof item === "number") return { x: item, y: item };
    const x = Number(item?.x ?? 0) || 0;
    const y = Number(item?.y ?? x) || 0;
    return { x, y };
  });
  const [topLeft, topRight = topLeft, bottomRight = topLeft, bottomLeft = topRight] = values;
  return [topLeft, topRight, bottomRight, bottomLeft].map((item) => ({
    x: Math.max(0, item.x),
    y: Math.max(0, item.y)
  }));
}

const CanvasContext =
  typeof CanvasRenderingContext2D !== "undefined" ? CanvasRenderingContext2D : undefined;

if (CanvasContext && !CanvasContext.prototype.roundRect) {
  Object.defineProperty(CanvasContext.prototype, "roundRect", {
    configurable: true,
    value(
      this: CanvasRenderingContext2D,
      x: number,
      y: number,
      width: number,
      height: number,
      radius?: RoundRectRadius | RoundRectRadius[]
    ) {
      const radii = normalizeRoundRectRadii(radius);
      const maxRadiusX = Math.abs(width) / 2;
      const maxRadiusY = Math.abs(height) / 2;
      const [topLeft, topRight, bottomRight, bottomLeft] = radii.map((item) => ({
        x: Math.min(item.x, maxRadiusX),
        y: Math.min(item.y, maxRadiusY)
      }));
      const right = x + width;
      const bottom = y + height;

      this.moveTo(x + topLeft.x, y);
      this.lineTo(right - topRight.x, y);
      this.quadraticCurveTo(right, y, right, y + topRight.y);
      this.lineTo(right, bottom - bottomRight.y);
      this.quadraticCurveTo(right, bottom, right - bottomRight.x, bottom);
      this.lineTo(x + bottomLeft.x, bottom);
      this.quadraticCurveTo(x, bottom, x, bottom - bottomLeft.y);
      this.lineTo(x, y + topLeft.y);
      this.quadraticCurveTo(x, y, x + topLeft.x, y);
      this.closePath();
    },
    writable: true
  });
}
